import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import xorWith from 'lodash/xorWith';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { getAriConfig, toAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import {
	fireTrackAnalytics,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { BACKLOG, BOARD } from '../model/constants.tsx';
import type { CardId } from '../model/types.tsx';
import {
	CUSTOM_FIELD_ID_REGEX,
	CARD_GROUP_DROPPABLE_TYPE,
	TRANSITION_ZONE_DROPPABLE_TYPE,
	DND_DROP_ZONES_ACTIVE_SPRINT,
	DND_DROP_ZONES_BACKLOG,
	DND_DROP_ZONES_BOARD,
	DND_DROP_ZONES_FUTURE_SPRINT,
	DND_OPERATION_MOVE_INSIDE_LIST,
	DND_OPERATION_MOVE_TO_ACTIVE_SPRINT,
	DND_OPERATION_MOVE_TO_BACKLOG,
	DND_OPERATION_MOVE_TO_BOARD,
	DND_OPERATION_MOVE_TO_FUTURE_SPRINT,
	DND_TARGET_BACKLOG_CARD,
	HEADER_DROPPABLE_ID_PREFIX,
	END_ANALYTICS_BACKLOG_TO_BOARD,
	END_ANALYTICS_ADD,
	END_ANALYTICS_BOARD_TO_BACKLOG,
	END_ANALYTICS_REMOVE,
	END_ANALYTICS_MOVE_BETWEEN_SPRINTS,
} from './constants/index.tsx';
import type { DndDestination, DndOperation, DndSource } from './types.tsx';

export const getCardListId = (cardListId: string): string => {
	if (cardListId && cardListId.startsWith(HEADER_DROPPABLE_ID_PREFIX)) {
		return cardListId.substr(HEADER_DROPPABLE_ID_PREFIX.length);
	}
	if (cardListId && cardListId.startsWith(CARD_GROUP_DROPPABLE_TYPE)) {
		return cardListId.split('::')[2];
	}
	if (cardListId && cardListId.startsWith(TRANSITION_ZONE_DROPPABLE_TYPE)) {
		return cardListId.split('::')[1];
	}
	return cardListId;
};

export const isDestinationTransitionZone = (droppableId: string) =>
	droppableId.startsWith(TRANSITION_ZONE_DROPPABLE_TYPE);

const getDndOperation = (
	srcDroppableId: string,
	destDroppableId: string,
	cardListId: string,
	isActiveSprint: (cardListId: string) => boolean,
): DndOperation => {
	if (srcDroppableId === destDroppableId) {
		return DND_OPERATION_MOVE_INSIDE_LIST;
	}
	if (cardListId === BACKLOG) {
		return DND_OPERATION_MOVE_TO_BACKLOG;
	}
	if (cardListId === BOARD) {
		return DND_OPERATION_MOVE_TO_BOARD;
	}
	if (isActiveSprint(cardListId)) {
		return DND_OPERATION_MOVE_TO_ACTIVE_SPRINT;
	}

	return DND_OPERATION_MOVE_TO_FUTURE_SPRINT;
};

const getDndSource = (
	srcDroppableId: string,
	destDroppableId: string,
	isActiveSprint: (cardListId: string) => boolean,
): DndSource => {
	if (srcDroppableId === BACKLOG) {
		return DND_DROP_ZONES_BACKLOG;
	}

	if (srcDroppableId === BOARD) {
		return DND_DROP_ZONES_BOARD;
	}

	if (isActiveSprint(srcDroppableId)) {
		return DND_DROP_ZONES_ACTIVE_SPRINT;
	}

	return DND_DROP_ZONES_FUTURE_SPRINT;
};

const getDndDestination = (
	cardListId: string,
	isActiveSprint: (cardListId: string) => boolean,
): DndDestination => {
	if (cardListId === BACKLOG) {
		return DND_DROP_ZONES_BACKLOG;
	}
	if (cardListId === BOARD) {
		return DND_DROP_ZONES_BOARD;
	}

	if (isActiveSprint(cardListId)) {
		return DND_DROP_ZONES_ACTIVE_SPRINT;
	}

	return DND_DROP_ZONES_FUTURE_SPRINT;
};

export const sendFailToMoveAnalytic = (
	analyticsEvent: UIAnalyticsEvent,
	cardIds: CardId[],
	error: Error,
	cardListId: string,
	isActiveSprint: (cardListId: string) => boolean,
) => {
	const srcDroppableId = analyticsEvent.payload?.attributes?.source ?? '';
	const destDroppableId = analyticsEvent.payload?.attributes?.target ?? '';

	const dndSource = getDndSource(srcDroppableId, destDroppableId, isActiveSprint);
	const dndDestination = getDndDestination(cardListId, isActiveSprint);
	const dndTarget = analyticsEvent.payload?.attributes?.type ?? DND_TARGET_BACKLOG_CARD;
	const operation = getDndOperation(srcDroppableId, destDroppableId, cardListId, isActiveSprint);

	fireOperationalAnalytics(analyticsEvent, 'cards moveFailed', {
		numCards: cardIds.length,
		errorMessage: error.message,
		operation,
		dndDestination,
		dndSource,
		dndTarget,
		isClientFetchError: isClientFetchError(error),
	});
};

export const sendSuccessToMoveAnalytic = (
	cardId: CardId,
	analyticsEvent: UIAnalyticsEvent,
	cardListId: string,
	isActiveSprint: (cardListId: string) => boolean,
) => {
	const srcDroppableId = analyticsEvent.payload?.attributes?.source ?? '';
	const destDroppableId = analyticsEvent.payload?.attributes?.target ?? '';

	const dndSource = getDndSource(srcDroppableId, destDroppableId, isActiveSprint);
	const dndDestination = getDndDestination(cardListId, isActiveSprint);
	const dndTarget = analyticsEvent.payload?.attributes?.type ?? DND_TARGET_BACKLOG_CARD;
	const operation = getDndOperation(srcDroppableId, destDroppableId, cardListId, isActiveSprint);

	fireTrackAnalytics(analyticsEvent, 'backlog updated', String(cardId), {
		operation,
		dndDestination,
		dndSource,
		dndTarget,
	});
};

export const getIdFromAri = (ari: string) => {
	const possibleAri = toAri(ari);
	if (possibleAri) {
		return getAriConfig(possibleAri).resourceId;
	}
	return ari;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObjectArrayEqual = (a: ArrayLike<any>, b: ArrayLike<any>): boolean =>
	isEmpty(xorWith(a, b, isEqual));

export const toShortCustomFieldId = (
	customFieldId: string | undefined | null,
): string | undefined => {
	if (!CUSTOM_FIELD_ID_REGEX.test(customFieldId ?? '')) {
		return undefined;
	}

	const fieldId = customFieldId?.match(CUSTOM_FIELD_ID_REGEX)?.[1];
	if (isNil(fieldId)) {
		return undefined;
	}

	return `cf[${fieldId}]`;
};

export const toArray = <T extends string>(x: T | T[]) => (Array.isArray(x) ? x : x.split(','));

export const getEndAnalyticsSprintActionTaken = (
	oldValId: string | undefined,
	newValId: string,
) => {
	if (oldValId === newValId) {
		return;
	}
	if (oldValId === BACKLOG) {
		return newValId === BOARD ? END_ANALYTICS_BACKLOG_TO_BOARD : END_ANALYTICS_ADD;
	}
	// if oldValId is BOARD, newValId would not be sprints
	if (oldValId === BOARD) {
		return END_ANALYTICS_BOARD_TO_BACKLOG;
	}
	if (newValId === BACKLOG) {
		return END_ANALYTICS_REMOVE;
	}
	return END_ANALYTICS_MOVE_BETWEEN_SPRINTS;
};
